// The Inbox kinds for app approvals (U13): a write to approve, an access
// request, and a business approval. Each registers with the Inbox's extension
// registry and draws its card in the one canvas; the card owns the decision.
// The record id comes from the item's ref, a typed field; an item without one
// shows the unavailable line rather than a guess.
import type { ComponentType } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import appApprovalsCopy from '@/i18n/ns/app-approvals';
import type { InboxItem } from '../inbox-api';
import { registerInboxKind } from '../inbox-extensions';
import { AppApprovalCard } from './approval-card';
import { BusinessApprovalCard } from './business-approval-card';
import { AppGrantCard } from './grant-card';
import { installAppsDecisionRoutes } from './apps-transport';

// The cards read and decide through the server's decision routes (business is the
// only kind with a producer today; the others answer unavailable there).
installAppsDecisionRoutes();

export const APP_INBOX_KINDS = {
  write: 'app_write_approval',
  grant: 'app_grant_request',
  business: 'app_business_approval',
} as const;

type DetailProps = { item: InboxItem; onSettled: () => void };

function detailFor(Card: ComponentType<{ id: string; onSettled?: () => void }>) {
  return function AppInboxDetail({ item, onSettled }: DetailProps) {
    const copy = useNamespaceCopy(appApprovalsCopy);
    const id = item.ref?.id;
    if (!id) return <p className="ws-app-card-note">{copy.unavailable}</p>;
    return <Card key={id} id={id} onSettled={onSettled} />;
  };
}

registerInboxKind({
  kind: APP_INBOX_KINDS.write,
  icon: 'connections',
  useLabel: () => useNamespaceCopy(appApprovalsCopy).kindWrite,
  Detail: detailFor(AppApprovalCard),
});
registerInboxKind({
  kind: APP_INBOX_KINDS.grant,
  icon: 'lock',
  useLabel: () => useNamespaceCopy(appApprovalsCopy).kindGrant,
  Detail: detailFor(AppGrantCard),
});
registerInboxKind({
  kind: APP_INBOX_KINDS.business,
  icon: 'users',
  useLabel: () => useNamespaceCopy(appApprovalsCopy).kindBusiness,
  Detail: detailFor(BusinessApprovalCard),
});
